import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MAT_LEGACY_DIALOG_DATA, MatLegacyDialogRef } from '@angular/material/legacy-dialog';
import { EncField, EncSort, Encoding, SortOrder, SortType } from '@mode-vegas/vegas';
import { isEqual } from 'lodash';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ChartFacade } from '@mode/charts/contract';
import { ChartTypes, FlamingoTypes } from '@mode/shared/contract-common';
import { AnalyticsEvent, AnalyticsService } from '@mode/shared/util-segment';
import { CompiledField } from '@mode/shared/util-switch';
import { SortModalListComponent } from './list/list.component';

import { FieldFormatHelpers } from '@mode/charts/util-vegas';
import * as FieldHelpers from '../helpers/field-helpers';
import type { SortAggFnOpt, SortConfig, SortFieldOpt, SortOpt, SortOrderOpt, SortTypeOpt } from './sort-modal.helpers';
import * as util from './sort-modal.helpers';

function isValidConfig(config?: SortConfig): config is SortConfig {
  return config != null && util.isValidConfig(config);
}

function defaultConfig(): SortConfig {
  return {
    type: SortType.DATA,
    order: SortOrder.ASC,
    callName: undefined,
    attrName: undefined,
  };
}

export interface SortModalDialogData {
  view: FlamingoTypes.ModeView;
  encoding: Encoding;
  field: EncField;
  fieldFormatMap: ChartTypes.FieldFormatMap | undefined;
  sort: EncSort | undefined;
  vizToken: string;
  onApply(sort: EncSort): void;
}

@Component({
  selector: 'mode-sort-modal',
  templateUrl: './sort-modal.component.html',
  styleUrls: ['./sort-modal.component.scss'],
})
export class SortModalComponent implements OnInit, OnDestroy {
  // Previous config (for reset)
  prevConfig: SortConfig | undefined;

  // Working set of changes
  config: SortConfig = defaultConfig();

  // Menu item selections
  sortType: SortTypeOpt | undefined;
  sortOrder: SortOrderOpt | undefined;
  sortField: SortFieldOpt | undefined;
  sortAggFn: SortAggFnOpt | undefined;

  // Fixed menus
  sortTypes = util.sortTypeOptions;
  sortOrders = util.sortOrderOptions;

  // Dynamic menus
  encodingSortFields: ReadonlyArray<SortFieldOpt> = [];
  otherSortFields: ReadonlyArray<SortFieldOpt> = [];
  sortAggFns: ReadonlyArray<SortAggFnOpt> = [];

  // Segment Tracking
  domainRequestStartTime: Date | undefined;

  // Type comes from Switch
  items: ReadonlyArray<unknown> = [];

  // Observables
  _destroy$ = new Subject<void>();

  formula!: CompiledField;
  error: string | undefined;
  sortDomainRefId = 'viz-sort-items-domain';
  orderedItems: unknown[] = [];
  formatter!: (name: string) => string | null;

  @ViewChild(SortModalListComponent) sortModalListComponent!: SortModalListComponent;
  constructor(
    private dialogRef: MatLegacyDialogRef<SortModalComponent>,
    private analyticsService: AnalyticsService,
    private chartFacade: ChartFacade,
    @Inject(MAT_LEGACY_DIALOG_DATA) private data: SortModalDialogData
  ) {
    const includeNested = util.fieldAllowsNestedSort(this.data.encoding, this.data.field);
    this.sortTypes = util.sortTypeOptions.filter((sortOpt) => {
      return (
        // Keep nested if:
        //   1) nested is enabled
        //   2) field is on {col,x} | {row,y}
        sortOpt.value === SortType.NESTED
          ? includeNested
          : // .. et al
            true
      );
    });
  }

  ngOnInit(): void {
    this.init();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  init() {
    const { view, field, sort, vizToken, encoding } = this.data;

    // Field to sort as a compiled formula
    this.formula = FieldHelpers.compileField(view, field);

    // Compiled and sorted view attrs for the by-field menu
    [this.encodingSortFields, this.otherSortFields] = FieldHelpers.makePartitionedByFieldsLists(view, encoding);

    // Value formatter for manual sort list
    this.formatter = this.makeFormatter();
    // Initial config built from an existing sort, if any
    const initConfig = util.makeSortConfig(view, field, sort);

    // Get domain items for byManual Sort
    this.requestByManualSortDomain(vizToken, field, sort, encoding);
    if (this.formula.alias) {
      this.chartFacade
        .createManualSortDomainValues(this.sortDomainRefId, this.formula.alias)
        .pipe(takeUntil(this._destroy$))
        .subscribe((items) => {
          if (items) {
            this.items = items;
            this.config.items = [...this.items];
            this.analyticsService.endTiming(AnalyticsEvent.Name.ManualSortDomainRequested);
            this.analyticsService.track(AnalyticsEvent.Name.ManualSortDomainLoaded, {
              location: 'Visual Explorer',
              modal_type: 'Sort',
              context: 'visualization',
              context_id: this.data.vizToken,
              domainValueCount: items.length,
            });
          }
        });
    }

    // Validate config on init so we can catch erroneous sorts and provide feedback
    this._checkDisplayInvalidSortErrorOnInit(initConfig);

    // Working state is expected to be valid at all times. If the initial config
    // is valid then we use it, otherwise set the working state to a default of
    // DATA ASC. This allows one to click "Apply" immediately to apply a valid sort
    this.config = defaultConfig();
    if (isValidConfig(initConfig)) {
      this.config = initConfig;
    }
    // Prev config may be undefined, valid, invalid.. which is alright. We disable
    // the Reset button if it's invalid or undef, indicating that you can't reset
    // to whatever the previous state was. If no sort was provided on init, then
    // there isn't anything to reset to
    this.prevConfig = undefined;
    if (sort != null) {
      this.prevConfig = {
        ...initConfig,
      };
    }

    // Sync up menus/selections
    this.sync();

    this.analyticsService.track(AnalyticsEvent.Name.ModalShown, {
      location: 'Visual Explorer',
      modal_type: 'Sort',
      context: 'visualization',
      context_id: this.data.vizToken,
    });
  }

  makeFormatter() {
    // Determine the appropriate format to use: Axis, Header or Pane
    const formatType = FieldFormatHelpers.determineFieldFormatTypeForField(this.data.encoding, this.data.field);

    // Get the appropriate field format
    const formatKey = FieldFormatHelpers.getFormatMapKeyByField(this.data.field);
    const fieldFormat = FieldFormatHelpers.resolveTextFormatByType(
      this.data.fieldFormatMap?.[formatKey] ?? FieldFormatHelpers.getDefaultFieldFormat(),
      formatType,
      this.data.field
    );
    // Return a function which formats the value based on the field format
    return (value: any) => {
      return FieldFormatHelpers.formatValue(value, this.formula, fieldFormat);
    };
  }

  requestByManualSortDomain(vizToken: string, field: EncField, sort: EncSort | undefined, encoding: Encoding): void {
    const sortValuesOrUndefined = sort?.type === SortType.MANUAL ? sort.items : undefined;
    this.chartFacade.requestByManualSortDomain(vizToken, this.sortDomainRefId, field, encoding, sortValuesOrUndefined);
    this.analyticsService.startTiming(AnalyticsEvent.Name.ManualSortDomainRequested, {
      location: 'Visual Explorer',
      modal_type: 'Sort',
      context: 'visualization',
      context_id: this.data.vizToken,
      domainValueCount: sortValuesOrUndefined?.length,
    });
  }

  sync(): void {
    this.sortType = FieldHelpers.find(this.sortTypes, (type) => {
      return type === this.config.type;
    });

    this.sortOrder = FieldHelpers.find(this.sortOrders, (order) => {
      return order === this.config.order;
    });

    // Showing field n' aggfn(s)
    if (this.showByFields) {
      this.sortField = FieldHelpers.findOrFirst([...this.encodingSortFields, ...this.otherSortFields], (attr) => {
        return attr.attrName === this.config.attrName;
      });

      // Sanity check. Should exist at this point
      const attr = this.sortField?.value;
      if (attr == null) {
        throw new Error(
          `sync(): Can not update aggregate function menu|selection if a ` +
            `by-field|nested field selection has not been made`
        );
      }

      this.sortAggFns = FieldHelpers.aggFnsForFormula(attr);
      this.sortAggFn = FieldHelpers.findOrFirst(this.sortAggFns, (value) => {
        return value === (this.config.callName ?? attr.defaultAggregate);
      });
    }
    // Not showing the field/agg menus, unset
    else {
      this.sortField = undefined;
      this.sortAggFn = undefined;
    }

    this.config.attrName = this.sortField?.value.attrName;
    this.config.callName = this.sortAggFn?.value;
  }

  private _clearInitErrors(): void {
    this.error = undefined;
  }

  private _checkDisplayInvalidSortErrorOnInit(config: SortConfig): void {
    if (!isValidConfig(config)) {
      // Init state is not valid then it's most likely
      // an invalid by-field sort: the underlying calc,
      // column, datatype.. etc. could have been changed
      // or removed resulting in an invalid formula
      const initialSort = this.data.sort;
      if (initialSort != null) {
        this.error = `The sort currently applied to this field is not supported in this dialogue.`;
      }
    }
  }

  ///
  /// Model

  get isResetDisabled(): boolean {
    if (this.config?.type === SortType.MANUAL) {
      return (
        isEqual(this.prevConfig, this.config) ||
        !isValidConfig(this.prevConfig) ||
        isEqual(
          this.prevConfig.items?.slice(0, util.MAX_SORTABLE_VALUES),
          this.config.items?.slice(0, util.MAX_SORTABLE_VALUES)
        )
      );
    } else {
      return isEqual(this.prevConfig, this.config) || !isValidConfig(this.prevConfig);
    }
  }

  get showByFields(): boolean {
    switch (this.config.type) {
      case SortType.NESTED:
      case SortType.FIELD: {
        return true;
      }
      default: {
        return false;
      }
    }
  }

  get showByManualSort(): boolean {
    switch (this.config.type) {
      case SortType.MANUAL: {
        return true;
      }
      default: {
        return false;
      }
    }
  }
  ///
  /// Change handlers

  /**
   * Handle sort type change
   * DATA | BYFIELD | ...
   */
  onChangeSortType = (item: SortTypeOpt): void => {
    this.config.type = item.value;
    this.sortType = item;
    this.sync();

    if (this.config.type === SortType.MANUAL) {
      this.analyticsService.track(AnalyticsEvent.Name.ManualSortSelected, {
        location: 'Visual Explorer',
        modal_type: 'Sort',
        context: 'visualization',
        context_id: this.data.vizToken,
        domainValueCount: this.config.items?.length,
      });
      const { vizToken, field, sort, encoding } = this.data;
      this.requestByManualSortDomain(vizToken, field, sort, encoding);
    }
  };

  /**
   * Handle sort order change
   * ACS | DESC
   */
  onChangeSortOrder = (item: SortOrderOpt): void => {
    this.config.order = item.value;
    this.sortOrder = item;
  };

  /**
   * Handle sort by-field|nested field change
   */
  onChangeField = (item: SortFieldOpt): void => {
    this.config.attrName = item.value.attrName;
    this.sortField = item;
    this.sync();
  };

  /**
   * Handle sort-by|nested field AGG change
   * AVG | COUNT | ...
   */
  onChangeAggregation = (item: SortAggFnOpt): void => {
    this.config.callName = item.value;
    this.sortAggFn = item;
  };

  /**
   * Handle manual sort
   */
  onChangeManualSortOrder = (orderedItems: unknown[]): void => {
    this.config.items = [...orderedItems];
  };

  ///
  /// Actions

  /**
   * Replace config with previous then resync the menus/selected options
   */
  reset(): void {
    if (isValidConfig(this.prevConfig)) {
      this.config = { ...this.prevConfig };
      this.sync();
      if (this.config.type === SortType.MANUAL) {
        this.sortModalListComponent.resetList(this.items);
      }
    }
  }

  /**
   * - Replace previous config with current
   * - Clear any UI errors we may have displayed on init
   * - Invoke provided onApply handler
   * - Close modal if requested
   */
  apply(closeAfterApply: boolean): void {
    this.prevConfig = { ...this.config };
    this._clearInitErrors();
    const sort = util.makeSort(this.data.field, this.config);
    if (sort != null) {
      this.analyticsService.track(AnalyticsEvent.Name.UiClicked, {
        location: 'Advanced Sort',
        action: closeAfterApply ? 'Apply and Close' : 'Apply',
      });

      const { onApply, vizToken, field, encoding } = this.data;
      // Don't bleed context (`this` ref data)
      onApply(sort);
      if (sort.type === SortType.MANUAL) {
        this.requestByManualSortDomain(vizToken, field, sort, encoding);
      }
      if (closeAfterApply) {
        this.close();
      }
    }
  }

  /**
   * Close the modal
   */
  close(): void {
    this.dialogRef.close();
  }

  /**
   * @description ngFor trackBy function for identifying a menu item by name
   */
  trackByName(_: number, opt: SortOpt): string {
    return opt.name;
  }
}
